import { useState } from "react";
import { FileText, Users, BookOpen, GraduationCap, ChevronRight } from "lucide-react";
import type { RequestState } from "@redux/state/shared-types";
import { Alert } from "@shared-kernel/primary/shared/alert";
import { BackButton } from "@shared-kernel/primary/shared/back-button/back-button";
import { Combobox } from "@shared-kernel/primary/shared/combobox/combobox";
import { CustomCard } from "@shared-kernel/primary/shared/custom-card/custom-card";
import { FormCard } from "@shared-kernel/primary/shared/form-card/form-card";
import { FormSaveActions } from "@shared-kernel/primary/shared/form-save-actions/form-save-actions";
import { Modal } from "@shared-kernel/primary/shared/modal/modal";
import { AlertModal } from "@shared-kernel/primary/shared/modal/alert-modal";
import { SuccessModal } from "@shared-kernel/primary/shared/success-modal/success-modal.content";
import { SectionHeader } from "@shared-kernel/primary/shared/section-header/section-header.content";
import { SimpleTable } from "@shared-kernel/primary/shared/simple-table";
import { Stepper } from "@shared-kernel/primary/shared/stepper/stepper";
import { Rating } from "@shared-kernel/primary/shared/rating/rating";
import { StackedAvatars } from "@shared-kernel/primary/shared/stacked-avatars/stacked-avatars";
import { TruncatedList } from "@shared-kernel/primary/shared/truncated-list/truncated-list.content";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { DangerSign } from "@shared-kernel/primary/shared/danger-sign/danger-sign";
import { DocumentButton } from "@shared-kernel/primary/shared/document-button/document-button.content";
import { DownloadLink } from "@shared-kernel/primary/shared/download-link/download-link.content";
import { FieldLimitCounter } from "@shared-kernel/primary/shared/field-limit-counter/field-limit-counter";
import { ThemedButton } from "@shared-kernel/primary/shared/themed-button/themed-button";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { ShowcaseBlock } from "@shared-kernel/primary/shared/design-system/shared/showcase-block";

const COMBOBOX_OPTIONS = [
  { label: "Marie Dubois", value: "1" },
  { label: "Jean Martin", value: "2" },
  { label: "Sophie Laurent", value: "3" },
];

const STEPPER_STEPS = [
  { id: 0, label: "Identité", title: "Identité", Icon: Users },
  { id: 1, label: "Formation", title: "Formation", Icon: BookOpen },
  { id: 2, label: "Validation", title: "Validation", Icon: GraduationCap },
];

const SAMPLE_AVATARS = [
  { firstName: "Marie", lastName: "Dubois" },
  { firstName: "Jean", lastName: "Martin" },
  { firstName: "Sophie", lastName: "Laurent" },
  { firstName: "Paul", lastName: "Durand" },
  { firstName: "Léa", lastName: "Petit" },
];

export const CustomComponentsSection = () => {
  const [comboboxValue, setComboboxValue] = useState("");
  const [stepperStep, setStepperStep] = useState(1);
  const [rating, setRating] = useState(3);
  const [modalOpen, setModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [fieldValue, setFieldValue] = useState("");
  const [demoProcessing, setDemoProcessing] = useState<RequestState>("idle");
  const [demoIsDirty, setDemoIsDirty] = useState(true);
  const [demoIsSubmitting, setDemoIsSubmitting] = useState(false);

  const runDemoSave = async () => {
    setDemoProcessing("pending");
    setDemoIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1100));
    setDemoIsSubmitting(false);
    setDemoProcessing("success");
    setDemoIsDirty(false);
    setTimeout(() => setDemoProcessing("idle"), 2100);
  };

  const runDemoSaveError = async () => {
    setDemoProcessing("pending");
    setDemoIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1100));
    setDemoIsSubmitting(false);
    setDemoProcessing("failed");
    setTimeout(() => setDemoProcessing("idle"), 2100);
  };

  return (
    <div className="space-y-8">
      <ShowcaseBlock
        id="alert"
        title="Alert"
        description="Bannière colorée pour information persistante (vs Toast éphémère)."
        importPath={`import { Alert } from "@shared-kernel/primary/shared/alert";`}
        sourcePath="src/shared-kernel/primary/shared/alert.tsx"
        useWhen="Information persistante affichée dans le contenu de la page (instruction, statut, avertissement)."
        avoidWhen="Confirmation éphémère après une action ponctuelle — préférer Toast."
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">variant=info</p>
            <Alert variant="info">Information complémentaire utile au contexte.</Alert>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">variant=success</p>
            <Alert variant="success">Action effectuée avec succès.</Alert>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">variant=warning</p>
            <Alert variant="warning">Attention : action à confirmer manuellement.</Alert>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">variant=error</p>
            <Alert variant="error">Une erreur bloquante empêche la sauvegarde.</Alert>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="back-button"
        title="BackButton"
        description="Retour de navigation utilisé dans les pages détail."
        importPath={`import { BackButton } from "@shared-kernel/primary/shared/back-button/back-button";`}
        sourcePath="src/shared-kernel/primary/shared/back-button/back-button.tsx"
      >
        <BackButton link="/design-system" label="Titre de la page" />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="combobox"
        title="Combobox"
        description="Select recherchable basé sur Command + Popover + Fuse.js."
        importPath={`import { Combobox } from "@shared-kernel/primary/shared/combobox/combobox";`}
        sourcePath="src/shared-kernel/primary/shared/combobox/combobox.tsx"
      >
        <div className="max-w-sm">
          <Combobox
            options={COMBOBOX_OPTIONS}
            value={comboboxValue}
            onChange={setComboboxValue}
            placeholder="Sélectionner un élève"
            search={{
              commandInputPlaceHolder: "Rechercher...",
              notFoundText: "Aucun élève",
            }}
          />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="combobox-etats"
        title="Combobox — États"
        description="États du Combobox via ses props disabled et isLoading."
        importPath={`import { Combobox } from "@shared-kernel/primary/shared/combobox/combobox";`}
        sourcePath="src/shared-kernel/primary/shared/combobox/combobox.tsx"
      >
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>default</Label>
            <Combobox
              options={COMBOBOX_OPTIONS}
              value=""
              onChange={() => undefined}
              placeholder="Sélectionner un élève"
              search={{
                commandInputPlaceHolder: "Rechercher...",
                notFoundText: "Aucun élève",
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>disabled</Label>
            <Combobox
              options={COMBOBOX_OPTIONS}
              value=""
              onChange={() => undefined}
              placeholder="Sélectionner un élève"
              search={{
                commandInputPlaceHolder: "Rechercher...",
                notFoundText: "Aucun élève",
              }}
              disabled
            />
          </div>
          <div className="space-y-1.5">
            <Label>loading</Label>
            <Combobox
              options={COMBOBOX_OPTIONS}
              value=""
              onChange={() => undefined}
              placeholder="Sélectionner un élève"
              search={{
                commandInputPlaceHolder: "Rechercher...",
                notFoundText: "Aucun élève",
              }}
              isLoading
            />
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="custom-card"
        title="CustomCard"
        description="Card avec titre, séparateur et contenu centré. Variantes header inline ou below."
        importPath={`import { CustomCard } from "@shared-kernel/primary/shared/custom-card/custom-card";`}
        sourcePath="src/shared-kernel/primary/shared/custom-card/custom-card.tsx"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <CustomCard
            title="Header inline"
            headerChildren={
              <Button size="sm" variant="outline">
                Action
              </Button>
            }
          >
            <p className="text-sm text-muted-foreground">Contenu de la carte.</p>
          </CustomCard>
          <CustomCard
            title="Header below"
            headerChildrenPosition="below"
            headerChildren={<p className="text-xs text-muted-foreground">Sous-titre ou métadonnée.</p>}
          >
            <p className="text-sm text-muted-foreground">Contenu de la carte.</p>
          </CustomCard>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="form-card"
        title="FormCard"
        description="Wrapper minimaliste de formulaire — padding standard, pas d'en-tête."
        importPath={`import { FormCard } from "@shared-kernel/primary/shared/form-card/form-card";`}
        sourcePath="src/shared-kernel/primary/shared/form-card/form-card.tsx"
      >
        <FormCard>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ds-form-card">Nom</Label>
              <Input id="ds-form-card" placeholder="Marie Dubois" />
            </div>
            <Button size="sm">Enregistrer</Button>
          </div>
        </FormCard>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="form-save-actions"
        title="FormSaveActions"
        description="Paire Annuler / Enregistrer pour les formulaires d'édition. Gère les états dirty, submitting et success."
        importPath={`import { FormSaveActions } from "@shared-kernel/primary/shared/form-save-actions/form-save-actions";`}
        sourcePath="src/shared-kernel/primary/shared/form-save-actions/form-save-actions.tsx"
        useWhen="Pied de formulaire d'édition (profil, paramètres) — alimenté par react-hook-form (isDirty, isSubmitting) et un RequestState Redux."
        avoidWhen="Création multi-étapes ou wizard — préférer un footer custom piloté par le Stepper."
      >
        <div className="space-y-4">
          <FormSaveActions
            processing={demoProcessing}
            isDirty={demoIsDirty}
            isSubmitting={demoIsSubmitting}
            onCancel={() => setDemoIsDirty(false)}
          />
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <p className="text-xs text-muted-foreground">Pilote :</p>
            <Button size="sm" variant="outline" onClick={() => setDemoIsDirty(true)} disabled={demoIsDirty || demoIsSubmitting}>
              Marquer dirty
            </Button>
            <Button size="sm" onClick={runDemoSave} disabled={!demoIsDirty || demoIsSubmitting}>
              Simuler succès
            </Button>
            <Button size="sm" variant="destructive" onClick={runDemoSaveError} disabled={!demoIsDirty || demoIsSubmitting}>
              Simuler erreur
            </Button>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="section-header"
        title="SectionHeader"
        description="Titre de section avec sous-titre optionnel. Casse uppercase."
        importPath={`import { SectionHeader } from "@shared-kernel/primary/shared/section-header/section-header.content";`}
        sourcePath="src/shared-kernel/primary/shared/section-header/section-header.content.tsx"
      >
        <SectionHeader title="Informations personnelles" subtitle="Données affichées sur le profil public" />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="simple-table"
        title="SimpleTable"
        description="Tableau sans pagination/tri/recherche. Pour des listes courtes."
        importPath={`import { SimpleTable } from "@shared-kernel/primary/shared/simple-table";`}
        sourcePath="src/shared-kernel/primary/shared/simple-table.tsx"
      >
        <SimpleTable
          headers={[{ title: "Nom" }, { title: "Rôle" }, { title: "Statut", classname: "text-right" }]}
          entries={[
            [{ value: "Marie Dubois" }, { value: "Élève" }, { value: "Actif", classname: "text-right" }],
            [{ value: "Jean Martin" }, { value: "Professeur" }, { value: "En attente", classname: "text-right" }],
          ]}
          emptyMessage="Aucune entrée"
        />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="stepper"
        title="Stepper"
        description="Indicateur d'étapes pour formulaires multi-pages. Conçu pour fond sombre."
        importPath={`import { Stepper } from "@shared-kernel/primary/shared/stepper/stepper";`}
        sourcePath="src/shared-kernel/primary/shared/stepper/stepper.tsx"
      >
        <div className="rounded-md bg-slate-800 p-3">
          <Stepper steps={STEPPER_STEPS} currentStep={stepperStep} onStepClick={setStepperStep} />
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setStepperStep(Math.max(0, stepperStep - 1))}>
            Précédent
          </Button>
          <Button size="sm" onClick={() => setStepperStep(Math.min(STEPPER_STEPS.length - 1, stepperStep + 1))}>
            Suivant
          </Button>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="rating"
        title="Rating"
        description="Notation 1 à 5 étoiles."
        importPath={`import { Rating } from "@shared-kernel/primary/shared/rating/rating";`}
        sourcePath="src/shared-kernel/primary/shared/rating/rating.tsx"
      >
        <div className="space-y-3">
          <Rating value={rating} onValueChange={setRating} />
          <Rating value={4} onValueChange={() => undefined} disabled />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="stacked-avatars"
        title="StackedAvatars"
        description="Avatars empilés avec tooltip listant les noms complets."
        importPath={`import { StackedAvatars } from "@shared-kernel/primary/shared/stacked-avatars/stacked-avatars";`}
        sourcePath="src/shared-kernel/primary/shared/stacked-avatars/stacked-avatars.tsx"
      >
        <div className="flex gap-6">
          <StackedAvatars items={SAMPLE_AVATARS.slice(0, 2)} />
          <StackedAvatars items={SAMPLE_AVATARS} max={3} />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="truncated-list"
        title="TruncatedList"
        description='Liste tronquée avec popover "+ N autres".'
        importPath={`import { TruncatedList } from "@shared-kernel/primary/shared/truncated-list/truncated-list.content";`}
        sourcePath="src/shared-kernel/primary/shared/truncated-list/truncated-list.content.tsx"
      >
        <div className="space-y-2 text-sm">
          <p>
            Spécialisations : <TruncatedList items={["Piano", "Violon", "Saxophone", "Batterie"]} />
          </p>
          <p>
            Court : <TruncatedList items={["Piano", "Violon"]} />
          </p>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="upload-button"
        title="UploadButton"
        description="Bouton d'upload avec validation, preview et états processing."
        importPath={`import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";`}
        sourcePath="src/shared-kernel/primary/shared/upload-button/upload-button.tsx"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <UploadButton
            onFileSelect={() => undefined}
            label="Document PDF"
            buttonText="Choisir un fichier"
            helpText="PDF uniquement, 5 Mo max"
          />
          <UploadButton onFileSelect={() => undefined} variant="compact" buttonText="Compact" />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="danger-sign"
        title="DangerSign"
        description="Pictogramme triangulaire d'alerte. Trois couleurs disponibles."
        importPath={`import { DangerSign } from "@shared-kernel/primary/shared/danger-sign/danger-sign";`}
        sourcePath="src/shared-kernel/primary/shared/danger-sign/danger-sign.tsx"
      >
        <div className="flex items-center gap-4">
          <DangerSign color="yellow" />
          <DangerSign color="orange" />
          <DangerSign color="red" />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="document-button"
        title="DocumentButton"
        description="Lien externe stylé bouton, ouvre un document dans un nouvel onglet."
        importPath={`import { DocumentButton } from "@shared-kernel/primary/shared/document-button/document-button.content";`}
        sourcePath="src/shared-kernel/primary/shared/document-button/document-button.content.tsx"
      >
        <div className="max-w-sm">
          <DocumentButton label="Convention de formation.pdf" url="#" />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="download-link"
        title="DownloadLink"
        description="Lien de téléchargement simple — wrapper sémantique."
        importPath={`import { DownloadLink } from "@shared-kernel/primary/shared/download-link/download-link.content";`}
        sourcePath="src/shared-kernel/primary/shared/download-link/download-link.content.tsx"
      >
        <DownloadLink to="#">
          <Button variant="outline" size="sm">
            <FileText className="mr-2 size-4" />
            Télécharger le PDF
          </Button>
        </DownloadLink>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="field-limit-counter"
        title="FieldLimitCounter"
        description="Indicateur de seuil min/max avec gradient de couleur — utilisé sous les textareas et compteurs métier."
        importPath={`import { FieldLimitCounter } from "@shared-kernel/primary/shared/field-limit-counter/field-limit-counter";`}
        sourcePath="src/shared-kernel/primary/shared/field-limit-counter/field-limit-counter.tsx"
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="ds-limit">Description (max 280)</Label>
              <FieldLimitCounter current={fieldValue.length} threshold={280} mode="max" />
            </div>
            <Input id="ds-limit" value={fieldValue} onChange={e => setFieldValue(e.target.value)} />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span>Heures déclarées (min 15) :</span>
            <FieldLimitCounter current={12} threshold={15} mode="min" suffix="h" />
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="themed-button"
        title="ThemedButton"
        description="Bouton de soumission de formulaire — couleur marque, désactivé si non-dirty."
        importPath={`import { ThemedButton } from "@shared-kernel/primary/shared/themed-button/themed-button";`}
        sourcePath="src/shared-kernel/primary/shared/themed-button/themed-button.tsx"
      >
        <div className="flex gap-3">
          <ThemedButton isDirty={true} label="Enregistrer" />
          <ThemedButton isDirty={false} label="Enregistrer" />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="modal"
        title="Modal (générique)"
        description="Wrapper Dialog avec footer Annuler/Confirmer standard."
        importPath={`import { Modal } from "@shared-kernel/primary/shared/modal/modal";`}
        sourcePath="src/shared-kernel/primary/shared/modal/modal.tsx"
        useWhen="Édition courte ou confirmation d'un changement réversible avec footer Annuler/Confirmer."
        avoidWhen="Action destructive irréversible — utiliser AlertModal qui bloque jusqu'à confirmation explicite."
      >
        <Button variant="outline" onClick={() => setModalOpen(true)}>
          Ouvrir Modal
        </Button>
        <Modal
          title="Confirmer la modification"
          body={<p className="text-sm">Voulez-vous appliquer les changements ?</p>}
          submitText="Confirmer"
          submitDisabled={false}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={() => setModalOpen(false)}
        />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="alert-modal"
        title="AlertModal"
        description="Wrapper AlertDialog pour confirmations destructives."
        importPath={`import { AlertModal } from "@shared-kernel/primary/shared/modal/alert-modal";`}
        sourcePath="src/shared-kernel/primary/shared/modal/alert-modal.tsx"
        useWhen="Confirmation d'une suppression ou action irréversible."
        avoidWhen="Édition simple ou confirmation réversible — utiliser Modal."
      >
        <Button variant="destructive" onClick={() => setAlertOpen(true)}>
          Ouvrir AlertModal
        </Button>
        <AlertModal
          title="Supprimer l'élève"
          body="Cette action est irréversible. L'élève et ses données seront supprimés."
          submitText="Supprimer"
          isOpen={alertOpen}
          onClose={() => setAlertOpen(false)}
          onSubmit={() => setAlertOpen(false)}
        />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="success-modal"
        title="SuccessModal"
        description="Modal de confirmation positive avec icône check et CTA pleine largeur."
        importPath={`import { SuccessModal } from "@shared-kernel/primary/shared/success-modal/success-modal.content";`}
        sourcePath="src/shared-kernel/primary/shared/success-modal/success-modal.content.tsx"
      >
        <Button variant="outline" onClick={() => setSuccessOpen(true)}>
          Ouvrir SuccessModal
        </Button>
        <SuccessModal
          isOpen={successOpen}
          onClose={() => setSuccessOpen(false)}
          title="Demande envoyée"
          description="Votre demande a bien été transmise. Nous reviendrons vers vous sous 48h."
        />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="creatable-select"
        title="CreatableSelect"
        description="Select avec création d'options à la volée. Nécessite react-hook-form (control)."
        importPath={`import { CreatableSelect } from "@shared-kernel/primary/shared/creatable-select/creatable-select";`}
        sourcePath="src/shared-kernel/primary/shared/creatable-select/creatable-select.tsx"
      >
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          Exemple non interactif — voir l'usage dans les formulaires d'édition (spécialisations, méthodologies). Wrapper autour de{" "}
          <code className="rounded bg-muted px-1 text-xs">react-select/creatable</code> +{" "}
          <code className="rounded bg-muted px-1 text-xs">react-hook-form Controller</code>.
        </p>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="pattern-item-liste"
        title="Pattern d'item de liste"
        description="Composition fréquente : avatar/icône + titre + métadonnée + chevron."
      >
        <div className="divide-y rounded-md border">
          {SAMPLE_AVATARS.slice(0, 3).map(p => (
            <button
              key={p.firstName}
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent"
            >
              <div className="flex items-center gap-3">
                <StackedAvatars items={[p]} />
                <div>
                  <p className="text-sm font-medium">
                    {p.firstName} {p.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">Élève — actif</p>
                </div>
              </div>
              <ChevronRight className="size-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      </ShowcaseBlock>
    </div>
  );
};
